import React, { Component, Fragment } from "react";
import { useGlobal } from "reactn";
import { Switch, Redirect, Route, useLocation } from "react-router-dom";
import RouteStatus from "./RouteStatus";
import loadable from "@loadable/component";
import classnames from "classnames";

import DisabledRoute from "components/DisabledRoute";
import MediaQuery from "components/MediaQuery";
import PremiumFAQ from "components/Accounts/PremiumFAQ";
import NoConsent from "components/Consent/NoConsent";
import OpenSearchRedirect from "components/OpenSearchRedirect";
import Error404 from "components/Error404";

import CovidReadMore from "components/CovidReadMore";
import AdWhitelist from "components/Ads/AdWhitelist";
import { useSideNav } from "hooks/general-hooks";
import { isAccountPage, isPremiumPage } from "../lib/general-helper";
import Login from "components/Accounts/Login";
import Signup from "components/Accounts/Signup";
import PerformanceAnalysisContainer from "./Pages/PerformanceReview/views/PerformanceAnalysisContainer";
import PerformanceAnalysisLoading from "./Pages/PerformanceReview/views/PerformanceAnalysisLoading";
import PerformanceAnalysisLandingPage from "components/Pages/PerformanceReview/views/Landing/PerformanceAnalysisLandingPage";
import VerificationContainer from "components/Pages/VerificationPage/views/VerificationContainer";
import ComboDatabase from "components/Champions/Combos/ComboDatabase";
import NotFoundUserSummonerProfile from "components/Accounts/NotFoundUserSummonerProfile/views/NotFoundUserSummonerProfile";
import Profiles200k from "components/SummonerProfiles/views/ProfileOverview/Profile200kSplash";
import MultiSearchMain from "components/MultiSearch/MultiSearchMain";
import { getSpecialTierLists } from "lib/specialGameMode";

// Loadable Components
const LandingPageContainer = loadable(() => import("components/Pages/LandingPage/LandingPageContainer"));
const DesktopAppSplashPage = loadable(() => import("components/DesktopAppSplashPage/DesktopAppSplashPage"));
const SupportUsPage = loadable(() => import("components/Pages/SupportUs/SupportUsPage"));
const LeaderboardsPage = loadable(() => import("components/Leaderboards/LeaderboardsPage"));
const StatsTables = loadable(() => import("components/StatsTables"));
const Champions = loadable(() => import("components/Champions"));
const SummonerProfile = loadable(() => import("components/SummonerProfiles/views/ProfilesMain/SummonerProfile"));
const LiveGameMain = loadable(() => import("components/SummonerProfiles/views/LiveGame/LiveGameMain"));
const Settings = loadable(() => import("components/Accounts/Settings/Settings"));
const RecoverAccount = loadable(() => import("components/Accounts/RecoverAccount/RecoverAccount"));
const PasswordReset = loadable(() => import("components/Accounts/PasswordReset/PasswordReset"));
const About = loadable(() => import("components/Pages/About/About"));
const FAQ = loadable(() => import("components/Pages/FAQ"));
const PrivacyPolicy = loadable(() => import("components/PrivacyPolicy"));
const PrivacyShield = loadable(() => import("components/PrivacyShield"));
const TermsOfService = loadable(() => import("components/TermsOfService"));
const Careers = loadable(() => import("components/Careers"));
const PremiumCheckout = loadable(() => import("components/Premium/views/PremiumCheckout"));
const LiveGameSplash = loadable(() => import("components/SummonerProfiles/views/LiveGame/LiveGameSplash"));
const EmailVerificationPage = loadable(() => import("components/Pages/EmailVerificationPage/EmailVerificationPage"));
const Items = loadable(() => import("components/Items/Items"));

const MainContent = (props) => {
  const [expandedSideNav] = useGlobal("expandedSideNav");
  const [verificationBar] = useGlobal("verificationBar");
  const location = useLocation();

  const [windowBreakpoint] = useGlobal("responsive");
  const [expandedSideNav2, setExpandedSideNav2] = useSideNav();
  const interactedWithSideNav = windowBreakpoint === "DESKTOP_LARGE" || manualSideNav;
  const [manualSideNav, setManualSideNav] = useGlobal("manualSideNav");

  const toggledSideNav = () => {
    !manualSideNav && setManualSideNav(true);
    (interactedWithSideNav || !expandedSideNav2) && setExpandedSideNav2(!expandedSideNav2, { saveToggle: true });
  };

  const mainContentClassNames = classnames({
    "verification-bar-active": verificationBar,
    collapsed: !expandedSideNav,
    expanded: expandedSideNav,
  });

  let tierLists = [
    "/lol/tier-list",
    "/lol/top-lane-tier-list",
    "/lol/jungle-tier-list",
    "/lol/mid-lane-tier-list",
    "/lol/adc-tier-list",
    "/lol/support-tier-list",
    "/lol/duo-tier-list",
    "/lol/combat",
    "/lol/objectives",
  ].concat(getSpecialTierLists());

  return (
    <div id="main-content" className={mainContentClassNames} ref={props.forwardedRef}>
      <div id="content-wrapper">
        <div id="content">
          <Switch>
            <Route exact path="/" component={LandingPageContainer} />
            <Route exact path="/app" component={DesktopAppSplashPage} />
            <Route exact path="/support-us" component={SupportUsPage} />
            <Redirect from="/discover-boost" to="/support-us" />
            <Route exact path="/lg-splash" component={LiveGameSplash} />
            <Route path="/verify" component={EmailVerificationPage} />
            <DisabledRoute path="/not-found-user-summoner-profile" component={NotFoundUserSummonerProfile} />
            <Route path="/lol/profile/:region/:summonerName" component={SummonerProfile} />
            <Route path="/lol/profile/:region/:summonerName/live-game/tags" component={LiveGameMain} />
            <Route path={tierLists} render={(routerProps) => <StatsTables {...routerProps} />} />
            <DisabledRoute path="/lol/combo-database" render={(props) => <ComboDatabase {...props} />} />
            <DisabledRoute path="/lol/customize-profiles" render={(props) => <Profiles200k {...props} />} />
            <Route
              path={["/lol/champions", "/lol/champion-leaderboards", "/lol/:language?/champions"]}
              render={() => <Champions />}
            />
            <Route path="/lol/items" render={() => <Items />} />
            <Route path="/leaderboards" component={LeaderboardsPage} />
            <Route path="/terms-of-service" render={(props) => <TermsOfService {...props} />} />
            <Route path="/ad-whitelist" render={(props) => <AdWhitelist {...props} />} />
            <Route path="/privacy-policy" render={(props) => <PrivacyPolicy {...props} />} />
            <Route path="/privacy-shield" render={(props) => <PrivacyShield {...props} />} />
            <Route path="/careers" render={(props) => <Careers {...props} />} />
            <Route path="/opensearch" render={(props) => <OpenSearchRedirect {...props} />} />
            <Route path="/multisearch" render={(props) => <MultiSearchMain {...props} />} />
            <DisabledRoute path="/settings" component={Settings} />
            <Route path="/recover-account" component={RecoverAccount} />
            <Route path="/reset-password" component={PasswordReset} />
            <Route path="/about" render={(props) => <About {...props} />} />
            <Route path="/faq" render={(props) => <FAQ {...props} />} />
            <Route path="/no-consent" render={(props) => <NoConsent {...props} />} />
            <Route path="/login" render={(props) => <Login {...props} />} />
            <Route path="/signup" render={(props) => <Signup {...props} />} />
            <DisabledRoute path="/boost/payment" component={PremiumCheckout} />
            <Redirect from="/boost(/*)?" to="/boost/payment" />
            <DisabledRoute path="/boost-faq" render={(props) => <PremiumFAQ {...props} />} />
            <DisabledRoute path="/reports/:season/year-in-review" render={(props) => <PerformanceAnalysisContainer />} />
            <DisabledRoute path="/year-in-review/loading" render={(props) => <PerformanceAnalysisLoading />} />
            <DisabledRoute path="/year-in-review" render={(props) => <PerformanceAnalysisLandingPage />} />
            <Route path="/verification" render={(props) => <VerificationContainer />} />
            <DisabledRoute path="/c19-read-more" render={(props) => <CovidReadMore {...props} />} />
            <RouteStatus statusCode={404} path="*">
              <Error404 />
            </RouteStatus>
          </Switch>
        </div>
      </div>
    </div>
  );
};

export default MainContent;
